import { useVisualElementsCounts } from 'hooks/queries/visualElements/useVisualElementsCounts'
import { ITEM_HEIGHT, ITEM_WIDTH, LARGE_ITEM_HEIGHT, LARGE_ITEM_WIDTH, WIDE_ASPECT_RATIO } from './TemplateSelect'
import Styles from './TemplateSelect.module.scss'
import { LoadingState } from './LoadingState'
import { CategoryCarousel } from './CategoryCarousel'
import { IFilters } from 'components/Wizards/DesignWizard/TemplateSelection/Filters'
import { EmptyState } from './EmptyState'
import { Newest } from './Newest'
import cx from 'classnames'
import { HeadingLevel } from 'blaze-ui/Heading/Heading'

interface Props {
  width: number
  height: number
  onSelect: (templateId: number) => void
  selectedIds?: number[]
  filters: IFilters
  customSort?: (a: string, b: string) => number
  includeNewest?: boolean
  largePreviews?: boolean
  headingLevel?: HeadingLevel
  carouselOffset?: number
}

const defaultSort = (a: string, b: string) => a.localeCompare(b)

const CATEGORIES_ABOVE_NEWEST = 3

export const Categories = ({
  filters,
  width,
  height,
  onSelect,
  selectedIds,
  customSort,
  headingLevel,
  carouselOffset,
  includeNewest = false,
  largePreviews = false,
}: Props) => {
  const { types, styles, categories: selectedCategories, query } = filters
  const { data, isLoading } = useVisualElementsCounts(
    {
      element_type: 'Template',
      width,
      height,
      type: types === 'all' ? undefined : ([...types] as string[]),
      style: styles === 'all' ? undefined : ([...styles] as string[]),
      category: selectedCategories === 'all' ? undefined : ([...selectedCategories] as string[]),
      query: query === '' ? undefined : query,
    },
    {
      cacheTime: 2 * 1000 * 60,
      staleTime: 2 * 1000 * 60,
    },
  )
  const categories = data ? Object.keys(data) : []

  const isWide = width / height > WIDE_ASPECT_RATIO

  let itemWidth
  let itemHeight

  if (largePreviews) {
    itemWidth = isWide ? undefined : LARGE_ITEM_WIDTH
    itemHeight = isWide ? LARGE_ITEM_HEIGHT : undefined
  } else {
    itemWidth = isWide ? undefined : ITEM_WIDTH
    itemHeight = isWide ? ITEM_HEIGHT : undefined
  }

  if (isLoading) {
    return (
      <div className={Styles.templateSelect}>
        <LoadingState
          width={width}
          height={height}
          itemWidth={itemWidth}
          itemHeight={itemHeight}
          categories={Array.from(filters.categories).length}
          carouselOffset={carouselOffset}
        />
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className={cx(Styles.templateSelect, Styles.emptyStateContainer)}>
        <EmptyState />
      </div>
    )
  }

  const sortedCategories = categories.sort(customSort || defaultSort)

  return (
    <div className={Styles.templateSelect}>
      {!includeNewest &&
        sortedCategories.map((category) => (
          <CategoryCarousel
            key={category}
            category={category}
            filters={filters}
            onSelect={onSelect}
            selectedIds={selectedIds}
            width={width}
            height={height}
            largePreviews={largePreviews}
            headingLevel={headingLevel}
            carouselOffset={carouselOffset}
          />
        ))}
      {includeNewest && (
        <>
          {sortedCategories.slice(0, CATEGORIES_ABOVE_NEWEST).map((category) => (
            <CategoryCarousel
              key={category}
              category={category}
              filters={filters}
              onSelect={onSelect}
              selectedIds={selectedIds}
              width={width}
              height={height}
              largePreviews={largePreviews}
              headingLevel={headingLevel}
              carouselOffset={carouselOffset}
            />
          ))}
          <Newest onPress={onSelect} width={width} height={height} />
          {sortedCategories.slice(CATEGORIES_ABOVE_NEWEST + 1).map((category) => (
            <CategoryCarousel
              key={category}
              category={category}
              filters={filters}
              onSelect={onSelect}
              selectedIds={selectedIds}
              width={width}
              height={height}
              largePreviews={largePreviews}
              headingLevel={headingLevel}
              carouselOffset={carouselOffset}
            />
          ))}
        </>
      )}
    </div>
  )
}
